/**
 * Public investment marketplace API — Rule 506(c) offerings only.
 *
 * Types mirror the engine's response shapes exactly (see engine
 * src/services/publicOfferings.js). Where the engine returns cents, the field
 * name says so and the value stays an integer.
 *
 * WHAT IS NOT IN THESE TYPES IS THE POINT. There is no `sharesSold`, no
 * `fundingProgress`, no investor count — the engine never sends them on this
 * surface, and declaring them here would invite a component to render a field
 * that will always be undefined and, worse, imply the data exists. A 506(c)
 * teaser may show the SIZE of a raise; it may not show how much of it has been
 * taken, or by whom.
 *
 * Everything here is reachable without an account. `apiRequest` still sends
 * credentials, so a signed-in visitor's lead is linked to their user, but no
 * call below requires a session.
 */

import { api } from './apiClient';

// ── Disclosures ─────────────────────────────────────────────────────────────

export interface OfferingDisclosures {
  /** The four mandatory 506(c) legends, served by the engine. */
  legends: string[];
  /** Required in the same view as any projected figure. */
  forwardLooking: string;
}

// ── Offerings ───────────────────────────────────────────────────────────────

/** Structural terms of the security. Never results, never amounts raised. */
export interface OfferingStructure {
  pricePerShareCents: number;
  totalShares: number;
  minimumShares: number;
  preferredReturnPercentage: number;
  performanceFeePercentage: number;
}

export interface OfferingEvent {
  id: string;
  title: string;
  featuredImageUrl: string | null;
  eventDate: string | null;
  venueName: string | null;
  venueCity: string | null;
}

export interface OfferingCard {
  id: string;
  /** Always '506c' on this surface — a private deal cannot reach it. */
  offeringType: string;
  title: string;
  description: string | null;
  securityType: string;

  /**
   * Whether this response carries figures at all.
   *
   * The engine OMITS every financial key for an unapproved or anonymous viewer rather
   * than nulling them, matching the marketplace card gate from task 41a. The four fields
   * below are therefore optional in the type, which is deliberate: TypeScript refuses to
   * let a component read them without first proving the gate passed, so a screen cannot
   * print a price it was never sent.
   */
  financialsVisible: boolean;
  targetRaiseCents?: number;
  minimumInvestmentCents?: number;
  structure?: OfferingStructure;
  offeringStartDate: string | null;
  closesAt: string | null;
  daysRemaining: number | null;
  event: OfferingEvent;
  disclosures: OfferingDisclosures;
}

/** Face value and house capacity only — live sales are not a term of the deal. */
export interface OfferingTicketTier {
  name: string;
  priceCents: number;
  quantityTotal: number;
}

export interface SimulatorRates {
  voletoPlatformFeeRate: number;
  stripeProcessingRate: number;
  /** Per transaction, not per dollar. */
  stripeFixedFeeCents: number;
  /** Of distributable profit, to investors. The remainder goes to the ORGANIZER, not Voleto. */
  investorSplitRate: number;
  /** The flat, one-time hurdle on principal. Platform-wide, not per offering. */
  preferredReturnRate: number;
  /**
   * The venue's negotiated cut of gross ticket revenue, where one applies.
   *
   * Charged in the same tier as the platform fee and card processing, ABOVE the investor
   * waterfall: the venue is an operating cost of the location, not an LP, and has no claim
   * on any tier of the split. Zero for nearly every offering, and the engine publishes it
   * as zero rather than omitting it so the simulator cannot silently fall back to
   * assuming there is none.
   *
   * This is the rate SNAPSHOTTED onto the event when it was published, not the venue's
   * current one. A projection computed at a renegotiated rate would show a deduction this
   * deal will not actually take.
   */
  venueRevenueShareRate: number;
  organizerSplitRate: number;
  performanceFeeRate: number;
}

export interface OfferingSimulator {
  rates: SimulatorRates;
  /** The only labels permitted on projected output. Both conditional. */
  resultLabel: string;
  qualifier: string;
  disclaimer: string;
}

export interface OfferingDetail extends OfferingCard {
  /** Gated with the figures: face values and capacity are the projection's inputs. */
  ticketTiers?: OfferingTicketTier[];
  /** Confirmed performers only; empty until both parties have countersigned. */
  lineup: { stageName: string; genre: string | null }[];
  /** Gated with the figures: absent means there is nothing to project a return from. */
  simulator?: OfferingSimulator;
}

export interface PublicOfferingList {
  offerings: OfferingCard[];
  disclosures: OfferingDisclosures;
}

export function listPublicOfferings(): Promise<PublicOfferingList> {
  return api.get<PublicOfferingList>('/opportunities/public');
}

export function getPublicOffering(offeringId: string): Promise<OfferingDetail> {
  return api.get<OfferingDetail>(`/opportunities/public/${offeringId}`);
}

// ── Lead capture ────────────────────────────────────────────────────────────

export interface LeadInput {
  offeringId?: string | null;
  name?: string | null;
  email: string;
  desiredAmountCents?: number | null;
  /** The screener answer. The engine refuses to store a lead without it. */
  accredited: boolean;
}

export interface LeadResult {
  leadId: string;
  kind: 'investor_inquiry' | 'mailing_list';
  outcome: 'inquiry_received' | 'mailing_list_only';
  offeringTitle: string | null;
  createdAt: string;
}

/**
 * "Apply to Invest" / mailing-list signup. Moves no money and creates no account.
 *
 * The engine decides which kind of record this becomes from `accredited`, and
 * discards the desired amount when the answer is no. The portal must not try to
 * pre-empt that decision — the split is enforced by a database constraint, and
 * duplicating the rule here would only create somewhere for the two to disagree.
 */
export function submitLead(input: LeadInput): Promise<LeadResult> {
  return api.post<LeadResult>('/opportunities/public/leads', input);
}

/**
 * An offering whose figures the viewer is entitled to see.
 *
 * The narrowing that makes the gate enforceable rather than advisory. Anything that
 * computes or renders money takes THIS type, so the only way to reach a price is through
 * offeringWithFinancials(), which returns null when the engine withheld them. A component
 * cannot forget the check, because there is no un-narrowed value to forget it on.
 */
export type OfferingWithFinancials = OfferingDetail & {
  financialsVisible: true;
  targetRaiseCents: number;
  minimumInvestmentCents: number;
  structure: OfferingStructure;
  ticketTiers: OfferingTicketTier[];
  simulator: OfferingSimulator;
};

/** Narrow a detail payload, or null if the viewer was not sent figures. */
export function offeringWithFinancials(
  offering: OfferingDetail | null | undefined,
): OfferingWithFinancials | null {
  if (!offering?.financialsVisible) return null;
  // Every gated key travels together: the engine adds them in one place (withFinancials)
  // and omits them in one place. Checking them all anyway costs nothing and means a
  // partial payload fails the narrowing instead of crashing a render.
  if (!offering.structure || !offering.ticketTiers || !offering.simulator) return null;
  if (offering.targetRaiseCents == null || offering.minimumInvestmentCents == null) return null;
  return offering as OfferingWithFinancials;
}

// The simulator is pure arithmetic and lives in its own module so it can be
// verified without a browser; re-exported here so callers have one import site.
export { simulate } from './offeringMath';
export type { SimulationInput, SimulationResult } from './offeringMath';
